'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowIcon from '@/components/ui/icons/arrow-icon';
import { useTranslations } from 'next-intl';
import Description from './description';
import ProductRatings from './ratings';
import ProductReviews from './reviews';
import { getProductReviewsV2 } from '@/services/product/reviews';
import useSWR from 'swr';

const ProductTabs = ({ description, sku, locale, entity_id }) => {
  const t = useTranslations('product');
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [, setIsMobileView] = useState(false);

  // ✅ Default active tab depends on whether description exists
  const [activeTab, setActiveTab] = useState(
    description ? 'description' : 'rating'
  );

  const { data } = useSWR(
    `product-reviews/${sku}/${locale}`,
    () => getProductReviewsV2(sku, locale),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );
  const availableTabs = useMemo(() => {
    const baseTabs = [
      description && {
        id: 'description',
        label: 'about_product',
      },
      {
        id: 'rating',
        label: 'Product Rating',
      },

      !!data?.data?.data?.length && {
        id: 'reviews',
        label: 'Product Reviews',
      },
    ].filter(Boolean); // remove falsy entries
    return baseTabs;
  }, [description, data?.data?.data?.length]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleAccordion = (id) => {
    setExpandedAccordion(expandedAccordion === id ? null : id);
  };

  const renderTabContent = useCallback(
    (tabId) => {
      switch (tabId) {
        case 'description':
          return <Description description={description} />;
        case 'rating':
          return (
            <ProductRatings
              reviews={data?.data?.data}
              t={t}
              entity_id={entity_id}
            />
          );
        case 'reviews':
          return <ProductReviews reviews={data?.data?.data} t={t} />;
        default:
          return null;
      }
    },
    [description, data?.data?.data, t]
  );

  return (
    <div className='mt-4 border-t border-gray-100 py-3 max-sm:mt-10'>
      {/* Desktop Tabs */}
      <div className='mb-3 hidden border-b border-gray-200 md:block'>
        <div className='hide-scrollbar flex overflow-x-auto'>
          {availableTabs.map((tab) => (
            <button
              key={tab.id}
              className={`px-6 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-b-2 border-[#1e2b55] text-[#1e2b55]'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {t(tab.label)}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Accordion */}
      <div className='space-y-2 md:hidden'>
        {availableTabs.map((tab) => (
          <div
            key={tab.id}
            className='overflow-hidden rounded-lg border border-gray-200'
          >
            <button
              className='flex w-full items-center justify-between p-3 text-left focus:outline-none'
              onClick={() => toggleAccordion(tab.id)}
            >
              <span className='font-medium'>{t(tab.label)}</span>
              <ArrowIcon size={18} className='text-gray-500' />
            </button>
            {expandedAccordion === tab.id && (
              <div className='animate-fadeIn border-t border-gray-200 p-3'>
                {renderTabContent(tab.id)}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop Tab Content */}
      <div className='animate-fadeIn hidden py-3 md:block'>
        {renderTabContent(activeTab)}
      </div>
    </div>
  );
};

export default ProductTabs;
